import { useEffect, useMemo, useState } from "react";
import {
  generatePath,
  Link,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { z } from "zod";
import { pathname } from "#src/config/pathname";
import { skipToContentTop } from "#src/utils/common";
import { apiErrorHandler, parseBadRequest } from "#src/utils/apiErrorHandler";
import paymentService from "#src/services/payment";
import {
  IPromotionCodeResponse,
  IValidPromotionCode,
} from "#src/services/payment/types";
import { PaymentMethodValue, VatTypeValue } from "#src/services/payment/enum";
import { ArrowRight, FlipBackward } from "@untitled-ui/icons-react";
import { ButtonV2 } from "#src/commonV2/Button";
import { LoadingStateV2 } from "#src/commonV2/LoadingState";
import { useCustomerInfoForm } from "#src/components/payment/v2/EnterInformation/CustomerInfo/useCustomerInfoForm";
import { useVatInvoiceForm } from "#src/components/payment/v2/EnterInformation/VatInvoice/useVatInvoiceForm";
import useFetch from "#src/hooks/useFetch";
import { PaymentEnterInformation } from "#src/components/payment/v2/EnterInformation";
import { PaymentProcessPayment } from "#src/components/payment/v2/ProcessPayment";

enum RenewTabEnum {
  FILL_FORM,
  COMPLETE_PAYMENT,
}

const WorkspaceBillingRenewSubscriptionPage = () => {
  const { t } = useTranslation("workspace", {
    keyPrefix: "billing.renewSubscription",
  });

  const { workspaceId } = useParams<"workspaceId">();
  const [searchParams] = useSearchParams();
  const filterProductId = searchParams.get("product_id");
  const filterExpiring = searchParams.get("expiring");

  if (!workspaceId) return null;

  // --------------- INITIALIZE ---------------

  const tabList: { key: RenewTabEnum; label: string }[] = [
    {
      key: RenewTabEnum.FILL_FORM,
      label: t("tab.enterInformation"),
    },
    {
      key: RenewTabEnum.COMPLETE_PAYMENT,
      label: t("tab.processPayment"),
    },
  ];

  const [currentTab, setCurrentTab] = useState<RenewTabEnum>(
    RenewTabEnum.FILL_FORM
  );

  // Tab's states
  // Fill Form tab
  const [subscribtionBanking, setSubscriptionBanking] = useState<Awaited<
    ReturnType<typeof paymentService.createSubscriptionBanking>
  > | null>(null);
  const [isLoadingSubscriptionBanking, setLoadingSubscriptionBanking] =
    useState<boolean>(false);

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodValue>(
    PaymentMethodValue.BANKING
  );

  const [exportVatInvoice, setExportVatInvoice] = useState<boolean>(false);

  const [productPlanWithPromo, setProductPlanWithPromo] = useState<
    { product_plan_id: string; duration: string; promo_code?: string }[]
  >([]);

  // --------------- HOOKS ---------------

  const fetchDefaultPaymentParams = useMemo<
    Parameters<typeof paymentService.retrieveWorkspacePaymentSettings>
  >(() => [workspaceId], [workspaceId]);

  const { result: defaultPayment } = useFetch(
    paymentService.retrieveWorkspacePaymentSettings,
    fetchDefaultPaymentParams
  );

  const fetchCalculatedPricingParams = useMemo<
    Parameters<typeof paymentService.calculatePricing>
  >(
    () => [
      workspaceId,
      {
        currency: "VND",
        product_plans: productPlanWithPromo,
      },
    ],
    [workspaceId, productPlanWithPromo]
  );

  const { result: calculatedPricing, isLoading: isLoadingCalculatedPricing } =
    useFetch(paymentService.calculatePricing, fetchCalculatedPricingParams);

  const {
    value: customerInfo,
    hasError: customerInfoHasError,
    setExternalError: setCustomerInfoExternalError,
    renderProps: customerInfoRenderProps,
  } = useCustomerInfoForm(defaultPayment);

  const {
    value: vatInvoice,
    hasError: vatInvoiceHasError,
    renderProps: vatInvoiceFieldRenderProps,
  } = useVatInvoiceForm(defaultPayment);

  // --------------- METHODS ---------------

  // Typeguard for promotion code
  function isPromoValid(
    promo: IPromotionCodeResponse
  ): promo is IValidPromotionCode {
    return promo.error_promo === null;
  }

  const onApplyPromocode = async (value: string) => {
    return paymentService
      .checkPromotionCode(workspaceId, { promo_code: value })
      .then((response) => {
        if (isPromoValid(response)) {
          const newProductPlanList = productPlanWithPromo.map((plan) =>
            response.only_plan.includes(plan.product_plan_id) &&
            !plan.promo_code
              ? {
                  ...plan,
                  promo_code: value,
                }
              : plan
          );

          setProductPlanWithPromo(newProductPlanList);

          return true;
        }
        return false;
      })
      .catch(apiErrorHandler);
  };

  const onRemovePromocode = (removeId: String) => {
    const newProductPlanList = productPlanWithPromo.map((plan) =>
      plan.product_plan_id === removeId
        ? {
            ...plan,
            promo_code: undefined,
          }
        : plan
    );

    setProductPlanWithPromo(newProductPlanList);
  };

  const onClickNextToCompletePayment = () => {
    if (
      workspaceId &&
      !customerInfoHasError &&
      (!exportVatInvoice || !vatInvoiceHasError)
    ) {
      setLoadingSubscriptionBanking(true);
      paymentService
        .renewSubscriptionBanking(workspaceId, {
          currency: "VND",
          payment_method: paymentMethod,
          // current form is only the renew one, this is_sign_contract & contract_type option is not included yet.
          is_sign_contract: false,
          // contract_type: ContractTypeValue,
          vat_invoice: exportVatInvoice,
          vat_information: exportVatInvoice
            ? vatInvoice.invoiceType === VatTypeValue.COMPANY
              ? {
                  name: vatInvoice.companyName,
                  tax_code: vatInvoice.taxCode,
                  address: vatInvoice.companyAddress,
                  type: VatTypeValue.COMPANY,
                }
              : {
                  name: vatInvoice.fullName,
                  identity_card: vatInvoice.citizenId,
                  address: vatInvoice.personalAddress,
                  type: VatTypeValue.PERSONAL,
                }
            : undefined,
          customer: {
            full_name: customerInfo.fullName,
            phone_number: customerInfo.phoneNumber,
            job_title: customerInfo.jobTitle,
            email: customerInfo.email,
          },
          product_plans: productPlanWithPromo,
        })
        .then((response) => {
          setCurrentTab(RenewTabEnum.COMPLETE_PAYMENT);
          skipToContentTop();
          setSubscriptionBanking(response);
        })
        .catch((error) => {
          if (
            error instanceof AxiosError &&
            error.response &&
            error.response.status === 400
          ) {
            const getErrorData = parseBadRequest(
              error.response.data,
              z.object({
                customer: z.object({
                  full_name: z.optional(z.array(z.string())),
                  phone_number: z.optional(z.array(z.string())),
                  email: z.optional(z.array(z.string())),
                }),
              })
            );

            const getCustomerError = (
              field: keyof typeof getErrorData.details.customer
            ): string | null => {
              const targetedField = getErrorData.details.customer[field];
              if (targetedField && targetedField.length > 0) {
                return targetedField[0];
              }
              return null;
            };

            const fullNameError = getCustomerError("full_name");
            if (fullNameError)
              setCustomerInfoExternalError.fullName(fullNameError);

            const phoneNumberError = getCustomerError("phone_number");
            if (phoneNumberError)
              setCustomerInfoExternalError.phoneNumber(phoneNumberError);

            const emailError = getCustomerError("email");
            if (emailError) setCustomerInfoExternalError.email(emailError);
          } else {
            apiErrorHandler(error);
          }
        })
        .finally(() => {
          setLoadingSubscriptionBanking(false);
        });
    } else {
      // This should never happen since if this happens the button will be disabled already.
      console.log("Must fill the information to continue");
    }
  };

  // --------------- COMPUTED ---------------

  const enterInformationHasError = useMemo(
    () => customerInfoHasError || (exportVatInvoice && vatInvoiceHasError),
    [customerInfoHasError, exportVatInvoice, vatInvoiceHasError]
  );

  // --------------- EFFECTS ---------------

  useEffect(() => {
    const initRenewList = async () => {
      if (workspaceId) {
        await paymentService
          .listWorkspacePaymentPlans(workspaceId, {
            status: filterExpiring === "1" ? "expiring,expired" : undefined,
            payment_method: undefined,
          })
          .then((response) => {
            const paymentFilteredByProductId = filterProductId
              ? response.filter((plan) => plan.product.id === filterProductId)
              : response;

            setProductPlanWithPromo(
              paymentFilteredByProductId.map((item) => ({
                product_plan_id: item.product_plan.id,
                duration: item.duration,
              }))
            );
          })
          .catch(apiErrorHandler);
      }
    };
    initRenewList();
  }, [workspaceId, filterProductId, filterExpiring]);

  // --------------- RENDER ---------------

  if (!workspaceId) return null;
  return (
    <>
      <div className="px-8 pt-5 pb-9 flex flex-col gap-5">
        {/* Header (Breadcrumb + title) */}
        <div className="flex flex-col gap-3">
          <Link
            to={generatePath(pathname.WORKSPACE_BILLING_SOLUTIONS, {
              workspaceId,
            })}
            className="flex items-center gap-2 text-md font-semibold no-underline text-brand-v2-700 
            hover:text-brand-v2-800 hover:bg-brand-v2-50 transition-colors rounded-lg w-fit"
          >
            <FlipBackward className="h-5 w-5 min-w-5" />
            {t("button.backToBilling")}
          </Link>
          <div className="flex justify-between gap-4">
            <span className="text-display-xs font-semibold text-gray-v2-900">
              {t("title")}
            </span>
            {currentTab === RenewTabEnum.FILL_FORM ? (
              <ButtonV2
                onClick={onClickNextToCompletePayment}
                disabled={enterInformationHasError}
              >
                {t("button.continue")}
                <ArrowRight className="h-5 w-5 min-w-5" />
              </ButtonV2>
            ) : null}
          </div>
        </div>
        {/* Header (Breadcrumb + title) end */}

        {/* Tabs (but we can't click it) */}
        <div className="flex gap-3 border-b border-gray-v2-200">
          {tabList.map((tab) => (
            <span
              key={tab.key}
              className={`pb-3 px-6 text-center flex-1 text-md font-semibold border-b-2 transition-colors no-underline ${
                // We're using numeric enums so this works like a charm
                tab.key <= currentTab
                  ? "text-brand-v2-700 border-brand-v2-600"
                  : "text-gray-v2 border-transparent"
              }`}
            >
              {tab.label}
            </span>
          ))}
        </div>
        {/* Tabs end */}

        {currentTab === RenewTabEnum.FILL_FORM ? (
          calculatedPricing === null || isLoadingCalculatedPricing ? (
            <LoadingStateV2 />
          ) : (
            <PaymentEnterInformation
              calculatedPricing={calculatedPricing}
              customerInfoRenderProps={customerInfoRenderProps}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              invoiceType={vatInvoice.invoiceType}
              vatInvoiceFieldRenderProps={vatInvoiceFieldRenderProps}
              exportVatInvoice={exportVatInvoice}
              setExportVatInvoice={setExportVatInvoice}
              onApplyPromocode={onApplyPromocode}
              onRemovePromocode={onRemovePromocode}
            />
          )
        ) : currentTab === RenewTabEnum.COMPLETE_PAYMENT ? (
          subscribtionBanking === null || isLoadingSubscriptionBanking ? (
            <LoadingStateV2 />
          ) : (
            <PaymentProcessPayment
              workspaceId={workspaceId}
              subscriptionBanking={subscribtionBanking}
              settingPath={pathname.TRUST_CENTER_CONFIG}
            />
          )
        ) : (
          "Wrong tab enum"
        )}
      </div>
    </>
  );
};

export default WorkspaceBillingRenewSubscriptionPage;
